import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { supabase } from '../../supabase'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, IconBtn, Input, Textarea, Label, Field, Row, Cluster, Pill,
  Loading, Empty, useUI,
} from './ui'

const EMPTY = { author: '', role: '', content: '', rating: 5, visible: true, avatar: '🌾' }
const AVATARS = ['🌾', '🐄', '🐎', '🐑', '🚜', '🐐', '🌿', '🏡']

const Star = styled.button`
  background: none; border: none; cursor: pointer;
  font-size: 1.45rem; padding: 2px;
  opacity: ${(p) => (p.$on ? 1 : 0.2)};
  transition: opacity 0.15s, transform 0.15s;
  &:hover { transform: scale(1.15); }
`
const AvatarBtn = styled.button`
  font-size: 1.4rem; padding: 5px 9px; border-radius: ${t.rSm}; cursor: pointer;
  border: 2px solid ${(p) => (p.$on ? t.gold : t.line)};
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  transition: all 0.16s;
  &:hover { border-color: ${t.gold}; }
`
const VisToggle = styled.button`
  display: flex; align-items: center; gap: 9px;
  padding: 9px 14px; border-radius: ${t.rSm}; cursor: pointer;
  font-family: ${t.font}; font-size: 0.83rem; font-weight: 700;
  background: ${(p) => (p.$on ? t.greenWash : t.redWash)};
  border: 1.5px solid ${(p) => (p.$on ? t.green : t.red)};
  color: ${(p) => (p.$on ? t.green : t.red)};
  transition: all 0.18s;
`
const Item = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-left: 3px solid ${(p) => (p.$hidden ? t.red : t.green)};
  border-radius: ${t.r};
  padding: 15px 18px;
  margin-bottom: 10px;
  box-shadow: ${t.shadow};
  opacity: ${(p) => (p.$hidden ? 0.62 : 1)};
  display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
  @media (max-width: 700px) { flex-direction: column; gap: 12px; }
`
const Head = styled.div`display: flex; align-items: center; gap: 9px; flex-wrap: wrap; margin-bottom: 6px;`
const Author = styled.strong`font-family: ${t.font}; font-size: 0.91rem; color: ${t.text};`
const Role = styled.span`font-family: ${t.font}; font-size: 0.75rem; color: ${t.textSoft};`
const Body = styled.p`
  font-family: ${t.font}; font-size: 0.85rem; color: ${t.textSoft};
  margin: 0; line-height: 1.65;
`

export default function AvisAdmin() {
  const { toast, confirm } = useUI()
  const [avis, setAvis] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(EMPTY)

  useEffect(() => { fetchAvis() }, [])

  const fetchAvis = async () => {
    setLoading(true)
    const { data } = await supabase.from('reviews').select('*').order('created_at', { ascending: false })
    setAvis(data || [])
    setLoading(false)
  }

  const saveAvis = async () => {
    if (!form.author.trim() || !form.content.trim()) return toast('Nom et avis requis', 'error')
    const payload = {
      author: form.author, role: form.role, content: form.content,
      rating: form.rating, visible: form.visible, avatar: form.avatar,
    }
    if (selected) {
      await supabase.from('reviews').update(payload).eq('id', selected.id)
      toast('Avis modifié')
      setSelected(null)
    } else {
      await supabase.from('reviews').insert([payload])
      toast('Avis ajouté')
      setAdding(false)
    }
    setForm(EMPTY)
    fetchAvis()
  }

  const toggleVisible = async (a) => {
    const current = a.visible !== false
    await supabase.from('reviews').update({ visible: !current }).eq('id', a.id)
    toast(!current ? 'Avis affiché sur le site' : 'Avis masqué du site')
    fetchAvis()
  }

  const deleteAvis = async (a) => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer cet avis ?',
      body: <>Le témoignage de <strong>{a.author}</strong> sera définitivement effacé.</>,
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    await supabase.from('reviews').delete().eq('id', a.id)
    toast('Avis supprimé')
    setSelected(null)
    fetchAvis()
  }

  const startEdit = (a) => {
    setSelected(a)
    setForm({ author: a.author, role: a.role || '', content: a.content, rating: a.rating, visible: a.visible ?? true, avatar: a.avatar || '🌾' })
    setAdding(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const close = () => { setAdding(false); setSelected(null); setForm(EMPTY) }

  const nbVisibles = avis.filter((a) => a.visible !== false).length

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Avis clients</PageTitle>
          <PageSub>{nbVisibles} visible(s) sur le site · {avis.length - nbVisibles} masqué(s)</PageSub>
        </div>
        <Btn $variant="gold" onClick={() => { setAdding(true); setSelected(null); setForm(EMPTY) }}>+ Ajouter un avis</Btn>
      </PageHead>

      {(adding || selected) && (
        <Stagger style={{ marginBottom: 20 }}>
          <Panel $accent>
            <PanelHead>
              <PanelTitle>{selected ? "Modifier l'avis" : 'Nouvel avis'}</PanelTitle>
              <Btn $variant="quiet" $size="sm" onClick={close}>✕ Fermer</Btn>
            </PanelHead>

            <Row $cols="1fr 1fr">
              <Field><Label>Nom du client *</Label><Input value={form.author} onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))} placeholder="ex : Jean-Pierre M." /></Field>
              <Field><Label>Rôle / titre</Label><Input value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} placeholder="ex : Éleveur bovin — Valais" /></Field>
            </Row>

            <Field>
              <Label>Emoji avatar</Label>
              <Cluster $gap="7px">
                {AVATARS.map((e) => (
                  <AvatarBtn key={e} $on={form.avatar === e} onClick={() => setForm((p) => ({ ...p, avatar: e }))}>{e}</AvatarBtn>
                ))}
                <Input value={form.avatar} onChange={(e) => setForm((p) => ({ ...p, avatar: e.target.value }))}
                  style={{ width: 64, textAlign: 'center', fontSize: '1.15rem' }} placeholder="✏️" />
              </Cluster>
            </Field>

            <Field>
              <Label>Texte de l'avis *</Label>
              <Textarea value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} placeholder="Témoignage du client…" style={{ minHeight: 104 }} />
            </Field>

            <Row $cols="1fr 1fr">
              <Field>
                <Label>Note</Label>
                <Cluster $gap="2px">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} $on={n <= form.rating} onClick={() => setForm((p) => ({ ...p, rating: n }))} aria-label={`${n} étoile(s)`}>⭐</Star>
                  ))}
                </Cluster>
              </Field>
              <Field>
                <Label>Visibilité sur le site</Label>
                <VisToggle $on={form.visible} onClick={() => setForm((p) => ({ ...p, visible: !p.visible }))}>
                  <span>{form.visible ? '👁' : '🙈'}</span>
                  {form.visible ? 'Visible sur le site' : 'Masqué du site'}
                </VisToggle>
              </Field>
            </Row>

            <Cluster $gap="10px">
              <Btn $variant="gold" style={{ flex: 1 }} onClick={saveAvis}>
                💾 {selected ? 'Enregistrer les modifications' : "Ajouter l'avis"}
              </Btn>
              {selected && <Btn $variant="danger" onClick={() => deleteAvis(selected)}>🗑 Supprimer</Btn>}
            </Cluster>
          </Panel>
        </Stagger>
      )}

      {loading ? (
        <Loading rows={3} h="96px" />
      ) : avis.length === 0 ? (
        <Empty icon="⭐" title="Aucun avis pour l'instant">
          Ajoutez les témoignages de vos clients pour les afficher sur le site.
        </Empty>
      ) : (
        avis.map((a, i) => {
          const hidden = a.visible === false
          return (
            <Stagger key={a.id} $i={i}>
              <Item $hidden={hidden}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Head>
                    <span style={{ fontSize: '1.1rem' }}>{a.avatar || '🌾'}</span>
                    <Author>{a.author}</Author>
                    {a.role && <Role>— {a.role}</Role>}
                    <span style={{ fontSize: '0.8rem' }}>{'⭐'.repeat(a.rating)}</span>
                    <Role>{new Date(a.created_at).toLocaleDateString('fr-FR')}</Role>
                    <Pill $tone={hidden ? 'red' : 'green'}>{hidden ? '🙈 Masqué' : '👁 Visible'}</Pill>
                  </Head>
                  <Body>{a.content}</Body>
                </div>
                <Cluster $gap="7px">
                  <IconBtn onClick={() => toggleVisible(a)} title={hidden ? 'Afficher sur le site' : 'Masquer du site'}>
                    {hidden ? '👁' : '🙈'}
                  </IconBtn>
                  <IconBtn onClick={() => startEdit(a)} title="Modifier">✏️</IconBtn>
                  <IconBtn onClick={() => deleteAvis(a)} title="Supprimer">🗑</IconBtn>
                </Cluster>
              </Item>
            </Stagger>
          )
        })
      )}
    </>
  )
}
